import { useRef, useState } from 'react'
import type { EventStore, PhotoStore } from '@application/ports.ts'
import { exportarFotos, importarFotos } from '@adapters/zip/fotos.ts'
import { restaurarRegistro } from './restaurar.ts'
import { es } from '../strings/es.ts'
import { VERSION } from '../version.ts'

function descargar(blob: Blob, nombre: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = nombre
  link.click()
  URL.revokeObjectURL(url)
}

const hoy = (): string => new Date().toISOString().slice(0, 10)

/** «2,4 MB», que es lo que quiere saber quien va a guardarlo en algún sitio. */
function pesa(bytes: number): string {
  if (bytes < 1_000_000) return `${Math.max(1, Math.round(bytes / 1000))} KB`
  return `${(bytes / 1_000_000).toFixed(1).replace('.', ',')} MB`
}

/**
 * Export and import the log. This is phase 1 work brought forward on purpose:
 * Chrome can evict an origin's storage, and losing six weeks of streak would cost
 * far more than the hour it takes to build a file download.
 *
 * Dos archivos y no uno. El registro es texto, pesa poco y conviene exportarlo
 * cada semana; las fotos son megas y no hace falta tanto. Juntarlos convertiría
 * la copia semanal en algo que da pereza, y una copia que da pereza no se hace.
 */
export function BackupPanel({
  store,
  photos,
  yaHayCasa,
  onChanged,
}: {
  store: EventStore
  photos: PhotoStore
  /**
   * Si esta tablet ya tiene una casa dentro. Importar encima no la sustituye —el
   * registro solo crece— así que las dos quedan dentro y los hijos salen
   * repetidos. Eso pasó de verdad, y no había aviso ninguno.
   */
  yaHayCasa: boolean
  onChanged: () => void
}) {
  const [message, setMessage] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const [avisado, setAvisado] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const fotosInput = useRef<HTMLInputElement>(null)

  const exportLog = async (): Promise<void> => {
    // The whole log, including whatever devices are already in it. A file
    // exported after restoring another one has to carry both histories: if it
    // only carried this tablet's own events, restoring onto a new tablet and
    // exporting again would shed the past at every hop.
    const events = await store.readAll()
    const payload = {
      app: 'mision-hogar',
      schemaVersion: 1,
      exportedAt: new Date().toISOString(),
      /** Which build wrote it. Worth having when a file from months ago has to
       *  be read by a newer app. */
      appVersion: VERSION,
      /** Which device wrote the file, not whose the events are. */
      deviceId: store.deviceId(),
      events,
    }
    descargar(
      new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }),
      `mision-hogar-${hoy()}.json`,
    )
    setMessage(es.backup.exported(events.length))
  }

  const importLog = async (file: File): Promise<void> => {
    const hecho = await restaurarRegistro(store, file)
    if (!hecho.ok) {
      setMessage(es.backup.sinRegistro)
      return
    }
    setMessage(es.backup.imported(hecho.puestos, hecho.yaEstaban))
    onChanged()
  }

  /** Con casa dentro, se avisa antes de abrir el selector de archivos. */
  const pedirArchivo = (): void => {
    if (yaHayCasa && !avisado) {
      setAvisado(true)
      return
    }
    fileInput.current?.click()
  }

  const exportFotos = async (): Promise<void> => {
    setOcupado(true)
    // Doscientas fotos tardan unos segundos. Sin esto el botón se apaga y parece
    // que la app se ha quedado colgada.
    setMessage(es.backup.fotosPreparando)
    try {
      const { blob, cuantas, bytes } = await exportarFotos(photos, new Date())
      if (cuantas === 0) {
        setMessage(es.backup.fotosVacio)
        return
      }
      descargar(blob, `mision-hogar-fotos-${hoy()}.zip`)
      setMessage(es.backup.fotosExported(cuantas, pesa(bytes)))
    } finally {
      setOcupado(false)
    }
  }

  const importFotos = async (file: File): Promise<void> => {
    setOcupado(true)
    setMessage(es.backup.fotosPreparando)
    try {
      const { puestas, yaEstaban, perdidas } = await importarFotos(photos, file)
      const faltan = perdidas > 0 ? ` ${es.backup.fotosFaltan(perdidas)}` : ''
      setMessage(es.backup.fotosImported(puestas, yaEstaban) + faltan)
      onChanged()
    } catch {
      // Cualquier cosa que no se pueda leer como copia de las fotos: un zip de
      // otra parte, un archivo cortado, un CRC que no cuadra.
      setMessage(es.backup.fotosIlegible)
    } finally {
      setOcupado(false)
    }
  }

  return (
    <section className="border-rule bg-surface mt-8 rounded border p-5">
      <h2 className="m-0 mb-1 text-lg font-bold">{es.backup.title}</h2>
      <p className="text-muted m-0 mb-1 text-sm">{es.backup.explain}</p>
      <p className="text-muted m-0 mb-4 text-sm">{es.backup.fuera}</p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="bg-teal rounded px-5 font-bold text-white"
          onClick={() => {
            void exportLog()
          }}
        >
          {es.backup.export}
        </button>
        <button
          type="button"
          className="border-rule rounded border px-5 font-bold"
          onClick={pedirArchivo}
        >
          {es.backup.importing}
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="application/json"
          className="hidden"
          aria-label={es.backup.importing}
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) void importLog(file)
            e.target.value = ''
          }}
        />
      </div>

      <h3 className="mt-6 mb-1 text-base font-bold">{es.backup.fotosTitle}</h3>
      <p className="text-muted m-0 mb-3 text-sm">{es.backup.fotosExplain}</p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="border-rule rounded border px-5 font-bold disabled:opacity-60"
          disabled={ocupado}
          onClick={() => {
            void exportFotos()
          }}
        >
          {es.backup.fotosExport}
        </button>
        <button
          type="button"
          className="border-rule rounded border px-5 font-bold disabled:opacity-60"
          disabled={ocupado}
          onClick={() => fotosInput.current?.click()}
        >
          {es.backup.fotosImport}
        </button>
        <input
          ref={fotosInput}
          type="file"
          accept="application/zip,.zip"
          className="hidden"
          aria-label={es.backup.fotosImport}
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) void importFotos(file)
            e.target.value = ''
          }}
        />
      </div>

      {avisado ? (
        <div role="alert" className="border-gold bg-gold-soft mt-4 rounded border-t-4 p-4">
          <p className="m-0 font-bold">{es.backup.yaHayCasa}</p>
          <p className="m-0 mt-1 text-sm">{es.backup.yaHayCasaExplica}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              className="bg-teal rounded px-5 font-bold text-white"
              onClick={() => {
                setAvisado(false)
                fileInput.current?.click()
              }}
            >
              {es.backup.yaHayCasaSigo}
            </button>
            <button
              type="button"
              className="border-rule rounded border px-5 font-bold"
              onClick={() => {
                setAvisado(false)
              }}
            >
              {es.backup.yaHayCasaNo}
            </button>
          </div>
        </div>
      ) : null}

      {message ? (
        <p role="status" className="text-mint mt-3 mb-0 text-sm font-bold">
          {message}
        </p>
      ) : null}
    </section>
  )
}
